import h from 'hyperscript';

import dragHandleIcon from '@disy/cadenza-icons/drag-indicator.svg';
import linearLayoutVisibilityOffIcon from '@disy/cadenza-icons/mobile-disabled.svg';
import linearLayoutVisibilityOnIcon from '@disy/cadenza-icons/mobile.svg';
import { on } from '@disy/lib/event-util';
import { createFormGroup } from '@disy/ui/form-group';
import { icon } from '@disy/ui/icon';
import { getSetting, getSettingOrSettingDefault } from '@disy/workbook.lib/settings';

import { createAutoToggle } from 'ui/auto-toggle/auto-toggle';
import { createIconToggle } from 'ui/icon-toggle/icon-toggle';

import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import 'cadenza/ui/help-tooltip/help-tooltip';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import { CommonViewSettingsKey } from 'cadenza/workbook/model/common-view-settings-key.gen';
import { WorksheetLinearLayoutViewOrder } from 'cadenza/workbook/model/worksheet-linear-layout-view-order.gen';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key.gen';
import { SortableList } from 'cadenza/workbook/sortable-list/sortable-list';
import { getViewIcon } from 'cadenza/workbook/view/view-util';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getViewById, getViewIdsByWorksheetId } from 'cadenza/workbook/workbook-core-selectors.state';
import { areViewsOrderedAutomatically } from 'cadenza/workbook/workbook-panel/settings/worksheet/linear-layout-order-utils';
import { LINEAR_LAYOUT_ORDER_KEY, getSortedViews } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';

import './views-order-panel.css';
import i18n from './views-order-panel.properties';

const COMPONENT_NAME = 'd-views-order-panel';

const VIEW_ORDER_KEY = WorksheetSettingsKey.toPersistenceKey('LINEAR_LAYOUT_VIEW_ORDER');
const VIEW_ORDER_VALUE_AUTO = WorksheetLinearLayoutViewOrder.toPersistenceKey('AUTO');
const VIEW_ORDER_VALUE_MANUAL = WorksheetLinearLayoutViewOrder.toPersistenceKey('MANUAL');
const LINEAR_LAYOUT_VISIBLE_KEY = CommonViewSettingsKey.toPersistenceKey('LINEAR_LAYOUT_VISIBLE');
const VIEW_ID = 'viewId';

export class ViewsOrderPanel extends HTMLElement {
  readonly #worksheetId: WorksheetId;

  constructor(worksheetId: WorksheetId) {
    super();
    this.#worksheetId = worksheetId;
  }

  connectedCallback() {
    this.classList.add(COMPONENT_NAME);
    const worksheetId = this.#worksheetId;
    let autoEnabled = areViewsOrderedAutomatically(worksheetId);

    const sortableList = new SortableList({
      pull: 'move',
      push: true,
      draggableInTouch: true,
      preserveHeight: true,
    });

    sortableList.append(...getSortedViewItems(autoEnabled, worksheetId));
    on(sortableList, 'update', () => {
      const viewIds: ViewId[] = [];
      for (const child of sortableList.children) {
        viewIds.push(child.getAttribute(VIEW_ID) as ViewId);
      }
      const commands = createChangeOrderSettings(viewIds);
      if (autoEnabled) {
        autoEnabled = false;
        autoToggle.pressed = autoEnabled;
        commands.unshift(createWorksheetAutoOrderCommand(worksheetId, autoEnabled));
      }
      executeAsCompositeCommand(commands);
    });

    const autoToggle = createAutoToggle({
      pressed: autoEnabled,
      onclick: () => {
        autoEnabled = autoToggle.pressed;
        const commands: Command[] = [];
        commands.push(createWorksheetAutoOrderCommand(worksheetId, autoEnabled));
        if (!autoEnabled) {
          const viewIds = getViewIdsByWorksheetId(getStore().getState(), worksheetId);
          if (viewIds.length > 0) {
            const view = getViewById(getStore().getState(), viewIds[0]);
            const orderValueExists = !!getSetting<number>(view, LINEAR_LAYOUT_ORDER_KEY);
            if (!orderValueExists) {
              const sortedViewIds = getSortedViews(true, worksheetId).map((viewWithMetaData) => viewWithMetaData.id);
              commands.push(...createChangeOrderSettings(sortedViewIds));
              executeAsCompositeCommand(commands);
              return;
            }
          }
        }

        executeAsCompositeCommand(commands);
        sortableList.replaceChildren(...getSortedViewItems(autoEnabled, worksheetId));
      },
    });
    autoToggle.slot = 'label-aside';

    this.append(
      createFormGroup([
        h('label', i18n('panel.title')),
        h('d-help-tooltip', { 'data-topic-id': 'worksheetSettings.linearLayout.order', slot: 'label-buttons' }),
        autoToggle,
        sortableList,
      ]),
    );
  }
}

function createWorksheetAutoOrderCommand(worksheetId: WorksheetId, auto: boolean) {
  return Command.CHANGE_WORKSHEET_SETTINGS({
    worksheetId,
    settings: {
      [VIEW_ORDER_KEY]: auto ? VIEW_ORDER_VALUE_AUTO : VIEW_ORDER_VALUE_MANUAL,
    },
  });
}

function createChangeOrderSettings(viewIds: ViewId[]) {
  const changeSettings: Command[] = [];
  for (let i = 0; i < viewIds.length; i++) {
    changeSettings.push(
      Command.CHANGE_VIEW_SETTINGS({
        viewId: viewIds[i],
        settings: { [LINEAR_LAYOUT_ORDER_KEY]: i },
      }),
    );
  }
  return changeSettings;
}

function getSortedViewItems(autoenabled: boolean, worksheetId: WorksheetId) {
  return getSortedViews(autoenabled, worksheetId).map((view) => viewToViewListItem(view));
}

function viewToViewListItem(view: ViewDto) {
  const dragHandle = icon(dragHandleIcon, { styleClass: `${COMPONENT_NAME}--drag-handle`, size: 'xs' });
  const labelWithIcon = createLabelWithIconElements(view);
  const linearLayoutVisibilityToggle = createLinearLayoutVisibilityToggle(view);
  const viewItemWrapper = h(
    `.${COMPONENT_NAME}--view-item-wrapper`,
    dragHandle,
    ...labelWithIcon,
    linearLayoutVisibilityToggle,
  );
  return h(`li.${COMPONENT_NAME}--view-item.d-hover-context`, { attrs: { [VIEW_ID]: view.id } }, viewItemWrapper);
}

function createLabelWithIconElements(view: ViewDto) {
  const typeIcon = getViewIcon(view, { size: 's' });
  const label = h(`span.ellipsis.${COMPONENT_NAME}--label`, view.name);
  return [typeIcon, label];
}

function createLinearLayoutVisibilityToggle(view: ViewDto) {
  const linearLayoutVisibilityToggle = createIconToggle(
    i18n('view.inMobileView.visible.hint'),
    icon(linearLayoutVisibilityOffIcon, { styleClass: `${COMPONENT_NAME}--visible-icon` }),
    icon(linearLayoutVisibilityOnIcon, { styleClass: `${COMPONENT_NAME}--visible-icon` }),
    {
      pressed: getSettingOrSettingDefault<boolean>(view, LINEAR_LAYOUT_VISIBLE_KEY),
      size: 'xs',
      onclick: () => toggleLinearLayoutVisibility(view, linearLayoutVisibilityToggle.pressed),
      styleClass: `${COMPONENT_NAME}--visibility-button`,
    },
  );
  return linearLayoutVisibilityToggle;
}

function toggleLinearLayoutVisibility(view: ViewDto, pressed: boolean) {
  const settings = {
    [LINEAR_LAYOUT_VISIBLE_KEY]: pressed,
  };
  getStore().dispatch(executeCommand(Command.CHANGE_VIEW_SETTINGS({ viewId: view.id, settings })));
}

function executeAsCompositeCommand(commands: Command[]) {
  getStore().dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })));
}

customElements.define(COMPONENT_NAME, ViewsOrderPanel);
